import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, CardBody, addToast } from "@heroui/react";
import { useNavigate } from "react-router-dom";
import {
  NftListForStakingApi,
  NftStakingApi,
  writeContractApi
} from "../../../../api/methods-marketplace";
import { formatUnits, encodeFunctionData } from "viem";
import NftStakingCardLoader from "../nft-staking-card/nft-staking-card-loader";
import NftStakingCard from "../nft-staking-card";
import { useWallet } from "../../../../context/WalletContext";
import nftStakingABI from "../../../../abis/nft-staking-abi.json";
import {
  waitForTransactionReceipt,
  writeContract,
  readContract
} from "@wagmi/core";
import { rainbowKitConfig } from "../../../../utils/config";

const CONTRACT_ADDRESS = process.env.REACT_APP_STAKING_CONTRACT_ADDRESS;

const getStakeInfo = async (address, isCustodialWallet, publicClient) => {
  if (!address) return null;

  try {
    if (isCustodialWallet) {
      if (!publicClient) {
        throw new Error("publicClient is required for custodial wallet");
      }

      const result = await publicClient.readContract({
        address: CONTRACT_ADDRESS,
        abi: nftStakingABI,
        functionName: "getStakeInfo",
        args: [address]
      });

      if (!result) return null;

      const [tokensStaked, rewards] = result;

      return {
        tokensStaked: tokensStaked?.map(Number) || [],
        rewards: rewards || BigInt(0)
      };
    } else {
      const result = await readContract(rainbowKitConfig, {
        address: CONTRACT_ADDRESS,
        abi: nftStakingABI,
        functionName: "getStakeInfo",
        args: [address]
      });

      if (!result) return null;

      const [tokensStaked, rewards] = result;

      return {
        tokensStaked: tokensStaked?.map(Number) || [],
        rewards: rewards || BigInt(0)
      };
    }
  } catch (err) {
    console.error("getStakeInfo error:", err);
    addToast({
      title: "Failed",
      description: "Failed to Fetch Stake Info",
      color: "danger",
      variant: "bordered"
    });
    return null;
  }
};

const UserTwitterCard = ({ address, isCustodialWallet, publicClient }) => {
  const [totalRewards, setTotalRewards] = useState(null);
  const { refreshBalance } = useWallet();

  useEffect(() => {
    async function load() {
      const p = await getStakeInfo(address, isCustodialWallet, publicClient);
      setTotalRewards(p?.rewards ?? BigInt(0));
    }
    load();
  }, [address, isCustodialWallet, publicClient]);

  const handleWithdraw = async () => {
    const rewardsFormatted = parseFloat(
      formatUnits(totalRewards.toString(), 18)
    ).toFixed(2);

    if (!rewardsFormatted || parseFloat(rewardsFormatted) <= 0) {
      addToast({
        title: "No Rewards",
        description: "You have no rewards to withdraw.",
        color: "warning",
        variant: "bordered"
      });
      return;
    }

    try {
      if (isCustodialWallet) {
        // ========================================
        // CUSTODIAL WALLET FLOW
        // ========================================
        if (!publicClient) {
          throw new Error("publicClient is required for custodial wallet");
        }

        const claimCallData = encodeFunctionData({
          abi: nftStakingABI,
          functionName: "claimRewards",
          args: []
        });

        const claimResult = await writeContractApi({
          address: CONTRACT_ADDRESS,
          encoded_data: claimCallData
        });

        if (!claimResult?.data?.tx_hash) {
          throw new Error("Transaction hash not received");
        }

        const txHash = claimResult.data.tx_hash;

        addToast({
          title: "Transaction Sent",
          description: "Waiting for confirmation...",
          color: "primary",
          variant: "bordered"
        });

        const receipt = await publicClient.waitForTransactionReceipt({
          hash: txHash
        });

        if (receipt.status !== "success") {
          addToast({
            title: "Withdraw Failed",
            description: "Transaction failed. Please try again.",
            color: "danger",
            variant: "bordered"
          });
          return;
        }

        addToast({
          title: "Withdraw Successful",
          description: "Your rewards have been withdrawn.",
          color: "success",
          variant: "bordered"
        });

        await refreshBalance();
        const updatedInfo = await getStakeInfo(
          address,
          isCustodialWallet,
          publicClient
        );
        setTotalRewards(updatedInfo?.rewards ?? BigInt(0));
      } else {
        // ========================================
        // NON-CUSTODIAL WALLET FLOW
        // ========================================
        const txHash = await writeContract(rainbowKitConfig, {
          address: CONTRACT_ADDRESS,
          abi: nftStakingABI,
          functionName: "claimRewards",
          args: []
        });

        addToast({
          title: "Transaction Sent",
          description: "Waiting for confirmation...",
          color: "primary",
          variant: "bordered"
        });

        const receipt = await waitForTransactionReceipt(rainbowKitConfig, {
          hash: txHash,
          confirmations: 1
        });

        if (receipt.status !== "success") {
          addToast({
            title: "Withdraw Failed",
            description: "Transaction failed. Please try again.",
            color: "danger",
            variant: "bordered"
          });
          return;
        }

        addToast({
          title: "Withdraw Successful",
          description: "Your rewards have been withdrawn.",
          color: "success",
          variant: "bordered"
        });

        await refreshBalance();
        const updatedInfo = await getStakeInfo(
          address,
          isCustodialWallet,
          publicClient
        );
        setTotalRewards(updatedInfo?.rewards ?? BigInt(0));
      }
    } catch (error) {
      const isUserRejected =
        error?.message?.includes("User rejected") ||
        error?.message?.includes("User denied");

      addToast({
        title: isUserRejected ? "Transaction Cancelled" : "Withdraw Failed",
        description: isUserRejected
          ? "You cancelled the transaction."
          : error?.response?.data?.message ||
            error?.message ||
            "Something went wrong. Try again.",
        color: isUserRejected ? "warning" : "danger",
        variant: "bordered"
      });
    }
  };

  if (totalRewards === null) {
    return (
      <Card className="max-w-[15rem] border-none bg-transparent" shadow="none">
        <CardBody>
          <p className="text-xs text-gray-500">Loading rewards...</p>
        </CardBody>
      </Card>
    );
  }

  const rewardsFormatted = parseFloat(
    formatUnits(totalRewards.toString(), 18)
  ).toFixed(2);

  return (
    <Card
      className="h-full border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow"
      shadow="sm"
    >
      <CardBody>
        <div className="p-6 flex items-center justify-between h-full gap-4">
          <div>
            <p className="text-2xl text-gray-900">
              <b>Total earnings:</b> {rewardsFormatted}{" "}
              <span className="text-lg font-medium text-yellowbuy">BAF</span>
            </p>
          </div>

          <Button
            size="sm"
            className="bg-yellowbuy border border-black text-black rounded-lg transition-all duration-200 hover:bg-[#e6cf00] hover:shadow-md hover:-translate-y-[1px] active:translate-y-0"
            onPress={handleWithdraw}
          >
            Withdraw
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

const NftUnStakingTabs = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isUnStaking, setIsUnStaking] = useState(false);
  const [stakingNftData, setStakingNftData] = useState([]);

  const { isConnected, address, isCustodialWallet, publicClient } = useWallet();
  const navigate = useNavigate();

  const fetchNftList = useCallback(async () => {
    if (!address) return;

    setIsLoading(true);
    try {
      const response = await NftListForStakingApi({
        method: "yes",
        address
      });

      setStakingNftData(response?.data?.data || []);
    } catch (error) {
      console.error("❌ Failed to fetch nft list:", error);
    } finally {
      setIsLoading(false);
    }
  }, [address]);

  useEffect(() => {
    if (address) fetchNftList();
  }, [address, fetchNftList]);

  const handleUnStakeClick = async (tokenId, nftAddress, onClose) => {
    if (!isCustodialWallet) {
      if (!isConnected || !address) {
        addToast({
          title: "Not Connected",
          description: "Please connect your wallet first.",
          color: "danger",
          variant: "bordered"
        });
        return;
      }
    }

    setIsUnStaking(true);

    try {
      if (isCustodialWallet) {
        if (!publicClient) {
          throw new Error("publicClient is required for custodial wallet");
        }

        const withdrawCallData = encodeFunctionData({
          abi: nftStakingABI,
          functionName: "withdraw",
          args: [[tokenId]]
        });

        const withdrawResult = await writeContractApi({
          address: CONTRACT_ADDRESS,
          encoded_data: withdrawCallData
        });

        if (!withdrawResult?.data?.tx_hash) {
          throw new Error("Transaction hash not received");
        }

        const txHash = withdrawResult.data.tx_hash;

        addToast({
          title: "Transaction Sent",
          description: "Waiting for confirmation...",
          color: "primary",
          variant: "bordered"
        });

        const receipt = await publicClient.waitForTransactionReceipt({
          hash: txHash
        });

        if (receipt.status !== "success") {
          addToast({
            title: "Unstake Failed",
            description: "Transaction failed. Please try again.",
            color: "danger",
            variant: "bordered"
          });
          setIsUnStaking(false);
          return;
        }

        updateStakingApi(
          {
            method: "unstake",
            collectionId: nftAddress,
            hash: txHash
          },
          onClose
        );
      } else {
        const txHash = await writeContract(rainbowKitConfig, {
          account: address,
          abi: nftStakingABI,
          address: CONTRACT_ADDRESS,
          functionName: "withdraw",
          args: [[tokenId]]
        });

        addToast({
          title: "Transaction Sent",
          description: "Waiting for confirmation...",
          color: "primary",
          variant: "bordered"
        });

        const receipt = await waitForTransactionReceipt(rainbowKitConfig, {
          hash: txHash,
          confirmations: 1
        });

        if (receipt.status !== "success") {
          addToast({
            title: "Unstake Failed",
            description: "Transaction failed. Please try again.",
            color: "danger",
            variant: "bordered"
          });
          setIsUnStaking(false);
          return;
        }

        updateStakingApi(
          {
            method: "unstake",
            collectionId: nftAddress,
            hash: txHash
          },
          onClose
        );
      }
    } catch (error) {
      setIsUnStaking(false);
      console.error("Unstake error:", error);

      const isUserRejected =
        error?.message?.includes("User rejected") ||
        error?.message?.includes("User denied");

      addToast({
        title: isUserRejected ? "Transaction Cancelled" : "Unstake Failed",
        description: isUserRejected
          ? "You cancelled the transaction."
          : error?.response?.data?.message ||
            error?.message ||
            "Something went wrong. Try again.",
        color: isUserRejected ? "warning" : "danger",
        variant: "bordered"
      });
    }
  };

  const updateStakingApi = async (data, closePopup) => {
    try {
      const response = await NftStakingApi(data);

      if (response) {
        addToast({
          title: "Unstaked Successfully",
          description: "Your NFT has been unstaked.",
          color: "success",
          variant: "bordered"
        });

        setIsUnStaking(false);
        closePopup();
        fetchNftList();
      }
    } catch (error) {
      setIsUnStaking(false);
      console.error("updateStakingApi error:", error);
    }
  };

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 w-full">
          {Array.from({ length: 12 }).map((_, index) => (
            <NftStakingCardLoader key={`loader-${index}`} />
          ))}
        </div>
      );
    }

    if (stakingNftData.length > 0) {
      return (
        <div className="w-full pb-12">
          <UserTwitterCard
            address={address}
            isCustodialWallet={isCustodialWallet}
            publicClient={publicClient}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 w-full pt-6">
            {stakingNftData.map((collection, index) => (
              <NftStakingCard
                data={collection}
                key={index}
                onClick={handleUnStakeClick}
                isUnStaking={isUnStaking}
              />
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"
            />
          </svg>
        </div>
        <h3 className="text-xl font-bold mb-2">
          No NFTs Available for Staking
        </h3>
        <Button
          onPress={() => navigate("/marketplace")}
          className="bg-yellowbuy border border-black rounded-lg"
        >
          Browse Marketplace
        </Button>
      </div>
    );
  };

  return <>{renderTabContent()}</>;
};

export default NftUnStakingTabs;
